/**
 * AdminShell — root layout widget for the /admin/* routes.
 *
 * Mounts:
 *   - `<AdminTopPill />` along the top edge
 *   - `<AdminLeftRail />` along the left edge
 *   - `<Outlet />` in the content slot for the active child route
 *
 * Composes module-local Tier 0 primitives from
 * `apps/admin/src/modules/platform/_components/`. The shell itself
 * is a cross-slice composition (route + identity), so it lives at
 * `apps/admin/src/widgets/admin-shell/` per ADR `frontend-0001`.
 *
 * **Entitlement gate.** Per `NAVIGATION_SPEC.md` + `INTEGRATION_GUIDE.md`,
 * every /admin/* route requires the Admin portal entitlement. The
 * shell defers to `hasAdminPortalEntitlement(me)` which honors the
 * tri-state `me.portals`:
 *   - `null` + DEV  (BE not yet shipped, local/Vitest) → ALLOW
 *   - `null` + PROD (BE not yet shipped, prod build)   → redirect to `/`
 *   - `[]`  (BE shipped, user has none)                → redirect to `/`
 *   - includes 'Admin'                                  → ALLOW
 *   - has portals but no Admin                          → redirect to `/`
 *
 * SCRUM-15 reviewer fix (2026-05-27): the `null` branch was unconditionally
 * permissive in round 1 and the redirect target was `/portal` (which had no
 * router entry, landing users on the 404 catch-all). Production now denies
 * by default — gated on `isDevBuild()` in `admin-portal-entitlement.ts` so
 * dev/Vitest keep the permissive fallback. Redirect target is `/` (the real
 * PMS root `AuthenticatedRootPage`); multi-portal users navigate further
 * via the in-account-menu portal switcher.
 *
 * **Keyboard shortcuts.** Cmd/Ctrl + 1..4 jumps to the four left-rail
 * destinations per `NAVIGATION_SPEC.md §7`. Cmd/Ctrl + K opens a
 * placeholder global-search route (real wiring lives in a future
 * SCRUM-116 follow-up commit). Vim-style `g + o/c/s/i` is opt-in
 * Phase 2 per the same §7 ("opt-in via Preferences → Keyboard");
 * out of scope for this commit.
 *
 * **Mobile drawer + Cmd/Ctrl + K live search** carve-outs: deferred
 * to a follow-up commit on this same SCRUM-15 branch. Tracked in
 * the file-level TODO blocks below + the SCRUM-116 ticket's
 * acceptance row.
 */

import * as React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AdminLeftRail,
  ADMIN_LEFT_RAIL_ITEMS,
  pathnameToAdminLeftRailActive,
} from './components/AdminLeftRail.js';
import { AdminTopPill } from './components/AdminTopPill.js';
import {
  MobileNavDrawer,
  type MobileNavItem,
} from '../../entities/navigation/MobileNavDrawer.js';
import { hasAdminPortalEntitlement } from '../../shared/auth/admin-portal-entitlement.js';
import { useSession } from '../../shared/hooks/useSession.js';
import { useMe } from '../../shared/auth/use-me.js';
import { useCompactMode } from '../../shared/hooks/useCompactMode.js';

/** Admin nav items for the compact mobile drawer (mirrors the left rail). */
const ADMIN_NAV_ITEMS: readonly MobileNavItem[] = ADMIN_LEFT_RAIL_ITEMS.map(
  (item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    href: item.route,
  }),
);

function navigateImperative(path: string): void {
  if (typeof window === 'undefined') return;
  window.location.assign(path);
}

/**
 * Wires Cmd/Ctrl + 1..4 keyboard jumps per `NAVIGATION_SPEC.md §7`.
 * Cmd/Ctrl + K is reserved for the future global-search wire-up —
 * the handler preventsDefault so the browser's address bar focus
 * doesn't intercept on macOS, but the action itself is a no-op
 * pending the search ticket.
 */
function useAdminKeyboardShortcuts(): void {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      // Modifier check — Cmd on Mac, Ctrl elsewhere.
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      if (event.key === '1') {
        event.preventDefault();
        navigate('/admin');
      } else if (event.key === '2') {
        event.preventDefault();
        navigate('/admin/companies');
      } else if (event.key === '3') {
        event.preventDefault();
        navigate('/admin/staff');
      } else if (event.key === '4') {
        event.preventDefault();
        navigate('/admin/imports');
      } else if (event.key === 'k' || event.key === 'K') {
        // SCRUM-116 follow-up: wire the global-search route. For
        // now preventDefault so the browser doesn't steal focus.
        event.preventDefault();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}

export interface AdminShellProps {
  /** Override the entitlement-check redirect for tests (no-op when undefined). */
  readonly redirect?: (path: string) => void;
}

export function AdminShell({
  redirect,
}: AdminShellProps = {}): React.ReactElement | null {
  const session = useSession();
  const me = useMe();
  useAdminKeyboardShortcuts();
  const compact = useCompactMode();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  // ── Entitlement gate ────────────────────────────────────────────
  // Three render branches mirror the dashboard auth shell:
  //
  //   loading        → minimal "Loading…" affordance
  //   unauthenticated → bounce to /login
  //   error          → text retry affordance
  //   authenticated  → check Admin entitlement, then render shell
  //
  React.useEffect(() => {
    if (session.status === 'unauthenticated') {
      (redirect ?? navigateImperative)('/login');
    }
  }, [session.status, redirect]);

  React.useEffect(() => {
    if (session.status !== 'authenticated') return;
    if (hasAdminPortalEntitlement(me)) return;
    // SCRUM-15 reviewer fix (2026-05-27): authenticated user lacks
    // Admin entitlement — bounce to `/`, the real PMS root
    // (`AuthenticatedRootPage`). The round-1 redirect target was
    // `/portal`, which had no matching route in `router.tsx` and
    // landed users on the catch-all 404. `/` is the PMS dashboard
    // (every authenticated user reaches it today), and the account-
    // menu portal switcher gives multi-portal users their lane.
    // When the dedicated `/portal` chooser route lands (SCRUM-113
    // follow-up), update the target.
    (redirect ?? navigateImperative)('/');
  }, [session.status, me, redirect]);

  if (session.status === 'loading') {
    return (
      <main
        className="flex min-h-screen items-center justify-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-secondary)' }}
      >
        <p>Loading…</p>
      </main>
    );
  }
  if (session.status === 'unauthenticated') return null;
  if (session.status === 'error') {
    return (
      <main
        className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center p-6 text-center"
        style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
      >
        <h1 className="mb-2 text-xl font-semibold">Couldn't load admin</h1>
        <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
          The session probe failed. Refresh the page, or sign in again.
        </p>
      </main>
    );
  }
  if (!hasAdminPortalEntitlement(me)) {
    // The redirect effect above handles navigation; return null for
    // the in-between paint.
    return null;
  }

  return (
    <div
      className="min-h-screen"
      style={{ background: 'var(--bg-canvas)', color: 'var(--fg-primary)' }}
    >
      {/* Center + clamp the shell to the desktop max-width on ultra-wide
          monitors, mirroring the PMS shell's page-frame max-width pattern:
          `max-w` + `mx-auto` + `px-6` gutters over the full-bleed `--bg-canvas`.
          Below 1440 px the content fills the viewport naturally. The
          artboard shell was drawn at a fixed width and never showed
          this case; PMS parity is the requirement here. */}
      <div
        className={`mx-auto flex min-h-screen max-w-[var(--page-max-width-desktop)] flex-col ${
          compact ? 'px-4' : 'px-6'
        }`}
      >
        <AdminTopPill compact={compact} onMenu={() => setDrawerOpen(true)} />
        {/* `mt-5` (20 px) gaps the content row below the floating
            top-pill capsule. No inner `overflow-auto`: the page scrolls
            at the viewport so the sticky top pill (`top-4`) + sticky
            left rail (`top-[88px]`) stay pinned. Per the artboard shell
            (`source/admin.jsx` AdminShell). On compact (mobile) the left
            rail is hidden and replaced by the hamburger-triggered
            MobileNavDrawer. */}
        <div className={`flex flex-1 min-h-0 ${compact ? 'mt-3' : 'mt-5'}`}>
          {!compact ? <AdminLeftRail /> : null}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
      {compact ? (
        <MobileNavDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          items={ADMIN_NAV_ITEMS}
          activeId={pathnameToAdminLeftRailActive(location.pathname)}
          user={me?.user ?? { id: '', email: '' }}
        />
      ) : null}
    </div>
  );
}
